import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedFetch } from '../utils/api';
import { formatDate } from '../utils/dateFormatter';
import { showSuccessToast, showErrorToast } from '../utils/toast';
import { shopifyAppBridge } from '../App';
import { calculateTotalSavings, formatPercentage } from '../utils/imageUtils';
import CompareModal from './shared/CompareModal';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatSizeKb(kb) {
  if (!kb || kb === 0) return '0 KB';
  if (kb < 1024) return `${parseFloat(kb).toFixed(2)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

function formatSizeBytes(bytes) {
  if (!bytes || bytes === 0) return '0 KB';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

/** Group flat job items into products. Returns an array sorted by first-seen order. */
function groupItemsByProduct(items) {
  const map = new Map();
  for (const item of items) {
    const key = item.productId || item.productTitle || 'unknown';
    if (!map.has(key)) {
      map.set(key, {
        productId: item.productId,
        productTitle: item.productTitle || 'Unknown Product',
        productHandle: item.productHandle || null,
        images: [],
      });
    }
    // Update handle if we get it in a later item
    if (item.productHandle && !map.get(key).productHandle) {
      map.get(key).productHandle = item.productHandle;
    }
    map.get(key).images.push(item);
  }
  return Array.from(map.values());
}

function getProductStatus(images) {
  const total = images.length;
  if (total === 0) return { label: 'No Images', tone: undefined };

  const completed = images.filter(i => i.status === 'completed' || i.status === 'optimized').length;
  const processing = images.filter(i => i.status === 'processing').length;
  const pending = images.filter(i => i.status === 'pending').length;
  const failed = images.filter(i => i.status === 'failed').length;
  const reverted = images.filter(i => i.status === 'reverted').length;
  const deleted = images.filter(i => i.status === 'deleted').length;

  if (deleted > 0) return { label: 'Deleted from Shopify', tone: 'critical' };
  if (reverted > 0) return { label: 'Reverted', tone: undefined };
  if (processing > 0) return { label: 'Processing...', tone: 'warning' };
  if (pending === total) return { label: 'Pending...', tone: undefined };
  if (pending > 0 && completed > 0) return { label: 'In Progress', tone: 'warning' };
  if (pending > 0 && completed === 0) return { label: 'Pending...', tone: undefined };
  if (completed === total) return { label: 'Fully Optimized', tone: 'success' };
  if (completed > 0 && failed > 0) return { label: 'Partially Optimized', tone: 'attention' };
  if (completed > 0) return { label: 'Partially Optimized', tone: 'attention' };
  if (failed === total) return { label: 'Failed', tone: 'critical' };
  return { label: 'Pending...', tone: undefined };
}

function getImageStatusBadge(status) {
  switch (status) {
    case 'completed':
    case 'optimized':
      return { label: 'Done', tone: 'success' };
    case 'processing':
      return { label: 'Processing...', tone: 'warning' };
    case 'pending':
      return { label: 'Pending...', tone: 'subdued' };
    case 'failed':
      return { label: 'Failed', tone: 'critical' };
    case 'reverted':
      return { label: 'Reverted', tone: undefined };
    case 'deleted':
      return { label: 'Deleted from Shopify', tone: 'critical' };
    default:
      return { label: 'Pending...', tone: 'subdued' };
  }
}

// Products shown per page in a job card's product list
const PRODUCTS_PER_PAGE = 20;

// Store completed job progress outside component to persist across unmounts
const completedJobsCache = new Map(); // jobId -> progress data (the page of products last shown)

/** job-progress URL for one page of products (the server returns only that page's items). */
function progressUrl(jobId, page) {
  return `/api/job-progress/${jobId}?page=${page}&pageSize=${PRODUCTS_PER_PAGE}`;
}

/** Normalize a job-progress response (first load, polling, page change, refresh after revert). */
function normalizeProgress(data, jobId) {
  return {
    id: data.id || jobId,
    status: data.status || 'pending',
    total: data.total || 0,
    totalProducts: data.totalProducts ?? null,
    completed: data.completed || 0,
    inProgress: data.inProgress || 0,
    failed: data.failed || 0,
    aborted: data.aborted || 0,
    skipped: data.skipped || 0,
    revertedImages: data.revertedImages || 0,
    revertedProducts: data.revertedProducts || 0,
    items: data.items || [],
    startTime: data.startTime || Date.now(),
    optimizationType: data.optimizationType || 'regular',
    jobSummary: data.jobSummary || null,
    // Whole-job values the server computes now that `items` holds only one page (null on legacy responses)
    pagination: data.pagination || null,
    productStats: data.productStats || null,
    itemStatusCounts: data.itemStatusCounts || null,
    processingProductKeys: data.processingProductKeys || null,
  };
}

/** Selection key of an image. Uses the DB id so a selection stays valid across pages. */
function selectionKey(img, fallback) {
  return img.imageDatabaseId ? `id:${img.imageDatabaseId}` : `noid:${fallback}`;
}

/** Product-level counts computed from a full item list (legacy responses without productStats). */
function computeProductStats(items) {
  const seen = Array.from(groupItemsByProduct(items).map(p => p.images));
  return {
    seen: seen.length,
    completed: seen.filter(imgs =>
      imgs.length > 0 &&
      imgs.every(i => !['pending', 'processing'].includes(i.status)) &&
      imgs.some(i => i.status === 'completed' || i.status === 'optimized')
    ).length,
    inProgress: seen.filter(imgs => imgs.some(i => i.status === 'processing' || i.status === 'pending')).length,
    failed: seen.filter(imgs => imgs.length > 0 && imgs.every(i => i.status === 'failed')).length,
    skipped: seen.filter(imgs =>
      imgs.length > 0 && imgs.every(i => i.status === 'skipped' || i.status === 'reverted' || i.status === 'deleted')
    ).length,
    // Reverted: products with AT LEAST 1 image reverted (not necessarily all)
    reverted: seen.filter(imgs => imgs.some(i => i.status === 'reverted')).length,
  };
}

/**
 * Single Job Card Component
 * Displays progress for one job with auto-scrolling activity logs
 */
function JobCard({ jobId, jobIndex, isLatestJob, onCreditChange }) {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aborting, setAborting] = useState(false);
  const intervalRef = useRef(null);
  const hadRunningJobRef = useRef(false);
  const isPollingRef = useRef(false);
  const fetchInFlightRef = useRef(false);
  const isCompletedRef = useRef(false);
  const consecutiveFailuresRef = useRef(0); // consecutive failed polls (network error, gateway error, bad reply...)
  const nextAttemptAtRef = useRef(0);       // back-off: earliest time of the next poll after failures
  const hasProgressRef = useRef(false);     // true once one poll has succeeded (then a hiccup keeps the last progress)
  const authenticatedFetchRef = useRef(authenticatedFetch);
  // Tracks the job's failed-item count so we only notify the parent (to refresh the
  // credits widget) the moment a NEW failure appears — a failed optimize attempt
  // refunds its credit server-side, so the balance changes right when this happens.
  const prevFailedCountRef = useRef(0);
  const onCreditChangeRef = useRef(onCreditChange);
  useEffect(() => { onCreditChangeRef.current = onCreditChange; }, [onCreditChange]);
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [revertingProductKey, setRevertingProductKey] = useState(null); // null | 'bulk' | productKey
  const [revertedProducts, setRevertedProducts] = useState(new Set());
  const [requestedAbort, setRequestedAbort] = useState(false);
  const [compareImage, setCompareImage] = useState(null);
  // Page of products the server is asked for. A ref so the polling closure always reads the latest page.
  const pageRef = useRef(1);
  const [pageLoading, setPageLoading] = useState(false);


  // Keep ref updated with latest authenticatedFetch
  useEffect(() => {
    authenticatedFetchRef.current = authenticatedFetch;
  }, [authenticatedFetch]);

  // Toggle product accordion
  const toggleProduct = useCallback((key) => {
    setExpandedProducts(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // Toggle image selection
  const toggleImageSelection = useCallback((itemKey) => {
    setSelectedImages(prev => {
      const next = new Set(prev);
      if (next.has(itemKey)) next.delete(itemKey); else next.add(itemKey);
      return next;
    });
  }, []);

  // Toggle all images in a product
  const toggleProductSelection = useCallback((images, productKey) => {
    setSelectedImages(prev => {
      const next = new Set(prev);
      const keys = images
        .map((img, idx) => ({ img, idx }))
        .filter(({ img }) => img.status === 'completed' || img.status === 'optimized')
        .map(({ img, idx }) => selectionKey(img, `${productKey}-${idx}`));
      const allSelected = keys.length > 0 && keys.every(k => prev.has(k));
      if (allSelected) keys.forEach(k => next.delete(k)); else keys.forEach(k => next.add(k));
      return next;
    });
  }, []);

  // Revert images — accepts either an array of DB IDs (for per-product revert)
  // or uses selectedImages to resolve DB IDs from job items (for bulk revert).
  // revertKey: identifies which button is loading ('bulk' or a product key string).
  const handleBulkRevert = useCallback(async (specificIds = null, productKeysToMark = [], revertKey = 'bulk') => {
    let imageIdsToRevert = [];

    if (Array.isArray(specificIds) && specificIds.length > 0) {
      imageIdsToRevert = specificIds;
    } else {
      if (selectedImages.size === 0) return;

      // Selection keys carry the image DB id ('id:<n>'), so images selected on other pages
      // (not in the currently loaded page of items) are reverted too.
      imageIdsToRevert = Array.from(selectedImages)
        .filter(k => k.startsWith('id:'))
        .map(k => k.slice(3));
    }

    if (imageIdsToRevert.length === 0) {
      showErrorToast('Could not find image IDs to revert. This job may have been started before the revert feature was added.');
      return;
    }

    setRevertingProductKey(revertKey);
    try {
      const response = await authenticatedFetchRef.current('/api/revert-images', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageIds: imageIdsToRevert })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to revert');

      // A large revert runs in the background on the server: just say so (nothing is reverted yet, so no UI update)
      if (data.queued) {
        showSuccessToast(data.message || 'Revert started in the background.');
        setSelectedImages(new Set());
        return;
      }

      showSuccessToast(`Successfully reverted ${data.reverted} image${data.reverted !== 1 ? 's' : ''}.`);

      // Mark only the specific reverted images — not the whole product — so partially reverted products show correctly
      if (imageIdsToRevert.length > 0) {
        const revertedIdSet = new Set(imageIdsToRevert.map(id => Number(id)));
        setProgress(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            items: prev.items.map(item =>
              item.imageDatabaseId && revertedIdSet.has(Number(item.imageDatabaseId))
                ? { ...item, status: 'reverted' }
                : item
            ),
          };
        });
      }

      // Invalidate cache and immediately re-fetch fresh job data so stats update
      // (reverted_images, reverted_products, completed) without a page refresh.
      completedJobsCache.delete(jobId);
      try {
        const refreshed = await authenticatedFetchRef.current(progressUrl(jobId, pageRef.current), { method: 'POST' });
        if (refreshed.ok) {
          const freshData = await refreshed.json();
          const normalizedFresh = normalizeProgress(freshData, jobId);
          if (normalizedFresh.pagination) pageRef.current = normalizedFresh.pagination.page;
          completedJobsCache.set(jobId, normalizedFresh);
          setProgress(normalizedFresh);
        }
      } catch (refreshErr) {
        // Non-fatal: optimistic UI update still shows reverted state
      }

      setSelectedImages(new Set());
    } catch (err) {
      showErrorToast(err.message || 'Failed to revert');
    } finally {
      setRevertingProductKey(null);
    }
  }, [selectedImages, progress]);

  useEffect(() => {
    if (!jobId) {
      setLoading(false);
      hadRunningJobRef.current = false;
      isPollingRef.current = false;
      isCompletedRef.current = false;
      setProgress(null);
      setError(null);
      return;
    }

    // Check if this jobId was already completed and cached
    const cachedProgress = completedJobsCache.get(jobId);

    if (cachedProgress) {
      // Show cached data immediately (no loading flash), then re-fetch once in background
      // to pick up status changes (reverts, deleted products) without requiring a full page reload.
      setProgress(cachedProgress);
      setLoading(false);
      setError(null);
      isCompletedRef.current = true;
      isPollingRef.current = false;
      // The cache holds the page that was showing, so keep asking the server for that page
      pageRef.current = cachedProgress.pagination?.page || 1;
      // Background refresh to pick up any status changes since cache was set
      authenticatedFetchRef.current(progressUrl(jobId, pageRef.current), { method: 'POST' })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data) return;
          const refreshed = normalizeProgress(data, jobId);
          // Ignore the response if the user already moved to another page meanwhile
          if (refreshed.pagination && refreshed.pagination.page !== pageRef.current) return;
          completedJobsCache.set(jobId, refreshed);
          setProgress(refreshed);
        })
        .catch(() => { /* silent — cached data still shows */ });
      return;
    }

    // Reset state for new job
    pageRef.current = 1;
    setPageLoading(false);
    fetchInFlightRef.current = false;
    hadRunningJobRef.current = false;
    isCompletedRef.current = false;
    consecutiveFailuresRef.current = 0;
    nextAttemptAtRef.current = 0;
    hasProgressRef.current = false;
    prevFailedCountRef.current = 0;
    setLoading(true);
    setError(null);
    setProgress(null);
    setExpandedProducts(new Set());
    setSelectedImages(new Set());
    setRevertedProducts(new Set());
    setRevertingProductKey(null);

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // A failed poll (network blip, 502/504 from the tunnel, expired token, server restart, a reply that is not JSON)
    // must never stop the polling for good: note it, slow down a little and try again. Polling only stops when the
    // server reports a finished job, the job is gone (404) or the component goes away.
    const registerFailure = (message) => {
      consecutiveFailuresRef.current += 1;
      const failures = consecutiveFailuresRef.current;
      nextAttemptAtRef.current = failures > 3 ? Date.now() + Math.min(10000, 1000 * 2 ** (failures - 3)) : 0; // up to 10 s apart
      if (failures >= 5 && !hasProgressRef.current) {
        setError(message || 'Failed to fetch progress'); // nothing to show yet: tell the user (polling goes on)
        setLoading(false);
      }
      console.warn(`[OptimizationProgress] progress poll failed (${failures} in a row): ${message} - will keep trying`);
    };

    const fetchProgress = async () => {
      if (!jobId || !isPollingRef.current) {
        return false;
      }
      
      if (isCompletedRef.current) {
        isPollingRef.current = false;
        return false;
      }

      // Back-off after failures: skip this tick, keep polling
      if (Date.now() < nextAttemptAtRef.current) {
        return true;
      }

      // A previous request is still pending: skip this tick (keep polling) rather
      // than stacking overlapping requests on a slow backend.
      if (fetchInFlightRef.current) {
        return true;
      }
      fetchInFlightRef.current = true;

      // A request that never answers must not block the polling forever: give up on it after 20 s and try again
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);

      try {
        const requestedPage = pageRef.current;
        const response = await authenticatedFetchRef.current(progressUrl(jobId, requestedPage), {
          method: 'POST',
          signal: controller.signal,
        });

        // An error page from a proxy/tunnel is HTML, not JSON: treat it as "no data", not as a crash
        const data = await response.json().catch(() => ({}));

        // The user switched page while this request was in flight: drop the stale page, keep polling
        if (requestedPage !== pageRef.current) {
          return true;
        }

        if (!response.ok) {
          if (response.status === 404) {
            if (hadRunningJobRef.current) {
              setProgress((prev) => prev ? { ...prev, status: 'completed' } : { status: 'completed', total: 0, completed: 0, failed: 0, inProgress: 0, items: [] });
              setError(null);
            } else {
              setError('Job not found');
            }
          } else {
            // Temporary problem (gateway error, expired token, restart...): keep polling, keep the last progress
            registerFailure(data.message || `HTTP ${response.status}`);
            return true;
          }
          setLoading(false);
          return false;
        }

        const normalizedData = normalizeProgress(data, jobId);
        // The server clamps an out-of-range page (e.g. the job has fewer products now)
        if (normalizedData.pagination) pageRef.current = normalizedData.pagination.page;

        setProgress(normalizedData);
        setError(null);
        setLoading(false);
        consecutiveFailuresRef.current = 0;
        nextAttemptAtRef.current = 0;
        hasProgressRef.current = true;

        // A new failure just appeared — the corresponding credit was refunded server-side
        // the moment processImage failed (see jobService.js), so refresh the balance now
        // instead of waiting for CreditsWidget's own ~20s poll.
        if (normalizedData.failed > prevFailedCountRef.current) {
          onCreditChangeRef.current?.();
        }
        prevFailedCountRef.current = normalizedData.failed;

        if (normalizedData.status === 'running' || normalizedData.status === 'pending') {
          hadRunningJobRef.current = true;
        }

        const isTerminal = normalizedData.status === 'completed' || normalizedData.status === 'failed' || normalizedData.status === 'cancelled';
        
        if (isTerminal) {
          isCompletedRef.current = true;
          completedJobsCache.set(jobId, normalizedData);
          hadRunningJobRef.current = false;
          return false;
        }
        
        return true;
      } catch (err) {
        // Network error or timeout (AbortError): keep polling
        registerFailure(err.name === 'AbortError' ? 'request timed out' : err.message);
        return true;
      } finally {
        clearTimeout(timeoutId);
        fetchInFlightRef.current = false;
      }
    };

    // Start polling
    isPollingRef.current = true;
    
    if (!intervalRef.current && !isCompletedRef.current) {
      // Initial fetch
      fetchProgress();
      
      // Set up polling interval
      intervalRef.current = setInterval(async () => {
        if (!isPollingRef.current || isCompletedRef.current) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return;
        }

        const shouldContinue = await fetchProgress();
        if (!shouldContinue) {
          isPollingRef.current = false;
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }, 2000);
    }

    return () => {
      isPollingRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [jobId]); // Removed authenticatedFetch from dependencies

  const handleAbort = useCallback(async () => {
    if (aborting) return;
    setAborting(true);
    setRequestedAbort(true);
    try {
      const response = await authenticatedFetchRef.current(`/api/abort-job/${jobId}`, { method: 'POST' });
      const data = await response.json();
      if (!response.ok) {
        showErrorToast(data.message || 'Failed to abort job');
        setAborting(false);
      } else {
        showSuccessToast("Abort initiated — current product's images will finish, then the job will stop.");
        // Optimistic update: mark as aborting immediately, don't reset aborting flag
        // so the button stays in "Aborting..." state until polling confirms cancellation
        setProgress(prev => prev ? { ...prev, status: 'aborting' } : prev);
        // intentionally NOT calling setAborting(false) — button stays disabled
      }
    } catch (err) {
      showErrorToast(err.message || 'Failed to abort job');
      setAborting(false);
    }
  }, [aborting, jobId]);

  // Load another page of products. Works for running and finished jobs (a finished job is not polled).
  // Buttons are disabled while loading, so only one page request is in flight at a time.
  const handlePageChange = useCallback(async (nextPage) => {
    const previousPage = pageRef.current;
    if (!jobId || nextPage === previousPage) return;
    pageRef.current = nextPage; // polling now asks for this page too
    setPageLoading(true);
    try {
      const response = await authenticatedFetchRef.current(progressUrl(jobId, nextPage), { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to load page');
      const normalized = normalizeProgress(data, jobId);
      if (normalized.pagination) pageRef.current = normalized.pagination.page;
      setProgress(normalized);
      if (['completed', 'failed', 'cancelled'].includes(normalized.status)) {
        completedJobsCache.set(jobId, normalized);
      }
    } catch (err) {
      pageRef.current = previousPage;
      showErrorToast(err.message || 'Failed to load page');
    } finally {
      setPageLoading(false);
    }
  }, [jobId]);

  // Early returns MUST come after all hooks
  if (!jobId) {
    return null;
  }

  if (loading && !progress) {
    return (
      <s-box data-job-card-id={jobId} padding="base" background="base" border="base" borderRadius="base">
        <s-stack gap="base">
          <s-stack direction="inline" gap="base" alignItems="center">
            <div className="shimmer-loader" style={{ height: 20, width: 140 }} />
            <div className="shimmer-loader" style={{ height: 20, width: 80, borderRadius: 12 }} />
          </s-stack>
          <div className="shimmer-loader" style={{ height: 8, width: '100%', borderRadius: 4 }} />
          <s-stack direction="inline" gap="large">
            <div className="shimmer-loader" style={{ height: 40, width: 60 }} />
            <div className="shimmer-loader" style={{ height: 40, width: 60 }} />
            <div className="shimmer-loader" style={{ height: 40, width: 60 }} />
          </s-stack>
          <s-box border="base" borderRadius="base" padding="base">
            <s-stack gap="small-200">
              {[1, 2, 3].map(i => (
                <s-stack key={i} direction="inline" gap="base" alignItems="center">
                  <div className="shimmer-loader" style={{ width: 36, height: 36, borderRadius: 4 }} />
                  <s-stack gap="small-100" style={{ flex: 1 }}>
                    <div className="shimmer-loader" style={{ height: 14, width: 160 }} />
                    <div className="shimmer-loader" style={{ height: 10, width: 100 }} />
                  </s-stack>
                </s-stack>
              ))}
            </s-stack>
          </s-box>
        </s-stack>
      </s-box>
    );
  }

  const {
    total = 0,
    completed = 0,
    inProgress = 0,
    failed = 0,
    aborted = 0,
    skipped = 0,
    // Dedicated reverted counters from the server (stored in jobs table, never mutated by revert)
    revertedImages: serverRevertedImages = 0,
    status = 'pending',
    items = [],
    totalProducts: totalProductsFromServer = null,
    optimizationType = 'regular',
    jobSummary = null,
    // Whole-job values from the server (items only holds the current page of products)
    pagination = null,
    productStats = null,
    itemStatusCounts = null,
    processingProductKeys = null
  } = progress || {};

  // Override status if we requested an abort but server hasn't updated yet
  const effectiveStatus = (status === 'running' || status === 'pending') && requestedAbort ? 'aborting' : status;

  // Use server-stored reverted counters (from jobs.reverted_images / jobs.reverted_products).
  // These are NEVER affected by recomputing from item statuses, so completed stays stable.
  // Fall back to counting from items for jobs that pre-date the new columns (revertedImages=0).
  const revertedImagesCount = serverRevertedImages > 0
    ? serverRevertedImages
    : itemStatusCounts
    ? itemStatusCounts.reverted
    : items.filter(i => i.status === 'reverted').length;

  // Progress: a completed job stays at 100% regardless of reverts.
  // `completed` is the stored success_items of the job, which does NOT drop when images are reverted (see
  // formatJobForApi), so reverted images are already inside it: adding revertedImagesCount again counted them twice
  // (e.g. 142 optimized + 37 reverted of 150 showed 119%). Progress = images that were processed / total, never above 100.
  const percentage = (status === 'completed' || status === 'reverted')
    ? 100
    : total > 0 ? Math.min(100, Math.round(((completed + failed) / total) * 100)) : 0;

  // Format job summary data
  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 KB';
    const kb = Math.round(bytes / 1024);
    if (kb < 1024) return `${kb} KB`;
    const mb = (kb / 1024).toFixed(2);
    return `${mb} MB`;
  };

  const getStatusBadge = () => {
    switch (status) {
      case 'completed':
        return <s-badge tone="success">Completed</s-badge>;
      case 'failed':
        return <s-badge tone="critical">Failed</s-badge>;
      case 'cancelled':
        return <s-badge tone="critical">Aborted</s-badge>;
      case 'aborting':
        return <s-badge tone="attention">Aborting...</s-badge>;
      case 'running':
        if (requestedAbort) return <s-badge tone="attention">Aborting...</s-badge>;
        return <s-badge tone="warning">Running</s-badge>;
      case 'reverted':
        return <s-badge tone="attention">Reverted</s-badge>;
      case 'deleted':
        return <s-badge tone="critical">Deleted</s-badge>;
      default:
        return <s-badge>Pending</s-badge>;
    }
  };

  return (
    <s-box
      data-job-card-id={jobId}
      padding="base"
      background="base"
      border="base"
      borderRadius="base"
    >
        <s-stack gap="base">
        {/* Card Header - Fixed */}
        <s-stack gap="base">
          <s-stack direction="inline" gap="base" alignItems="center" justifyContent="space-between">
            <s-stack direction="inline" gap="base" alignItems="center">
              {/* TASK 4: Better job titles */}
              <s-heading>
                {isLatestJob 
                  ? (status === 'completed' || status === 'failed' ? 'Previous Job' : 'Current Job')
                  : 'Previous Job'}
              </s-heading>
              {getStatusBadge()}
              <s-badge tone="info">{optimizationType.charAt(0).toUpperCase() + optimizationType.slice(1)}</s-badge>
            </s-stack>

            {/* "View All" link moved into product accordion header for cleaner layout */}
          </s-stack>

          {/* Abort button row — only when job is actively running — MORE PROMINENT PLACEMENT */}
          {(effectiveStatus === 'running' || effectiveStatus === 'pending' || effectiveStatus === 'aborting') && total > 1 && (
            <s-box padding="base" background="subdued" borderRadius="base">
              <s-stack direction="inline" gap="base" alignItems="center" justifyContent="space-between">
                <s-stack gap="none">
                  <s-text type="strong">Action Required?</s-text>
                  <s-text tone="subdued" size="small">
                    {effectiveStatus === 'aborting' 
                      ? "Current product's images will finish and then job will stop." 
                      : "You can stop the optimization at any time. Current product's images will finish and then job will stop."}
                  </s-text>
                </s-stack>
                <s-button
                  tone="critical"
                  onClick={handleAbort}
                  disabled={aborting || effectiveStatus === 'aborting'}
                  loading={aborting || effectiveStatus === 'aborting'}
                  className="abort-job-btn"
                >
                  {effectiveStatus === 'aborting' || aborting ? 'Stopping…' : 'Stop Optimization'}
                </s-button>
              </s-stack>
            </s-box>
          )}
        </s-stack>

        {/* Progress Bar - Fixed */}
        <s-stack gap="small">
          <s-stack direction="inline" justifyContent="space-between">
            <s-text>Progress: {percentage}%</s-text>
            {status === 'cancelled' && (
              <s-text tone="critical" size="small" type="strong">Aborted (Partial)</s-text>
            )}
          </s-stack>
          <div className="progress-bar-container">
            <div
              className={`progress-bar-fill ${
                status === 'completed' ? 'progress-completed' : 
                status === 'cancelled' ? 'progress-failed' : 
                'progress-in-progress'
              }`}
              style={{
                width: `${Math.max(0, Math.min(100, percentage))}%`,
                backgroundColor: status === 'cancelled' ? '#d82c0d' : undefined
              }}
            />
          </div>
        </s-stack>

        {/* Job Summary - Show job-level details */}
        {jobSummary && (
          <s-box padding="base" background="subdued" borderRadius="base">
            <s-stack gap="small">
              <s-text type="strong">Job Summary</s-text>
              <s-grid gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))" gap="base">
                <s-stack gap="small">
                  <s-text color="subdued" size="small">Started At</s-text>
                  <s-text size="small">
                    {jobSummary.startedAt ? formatDate(jobSummary.startedAt) : 'In progress...'}
                  </s-text>
                </s-stack>
                {jobSummary.completedAt && (
                  <s-stack gap="small">
                    <s-text color="subdued" size="small">
                      {status === 'cancelled' ? 'Aborted At' : 'Completed At'}
                    </s-text>
                    <s-text size="small">{formatDate(jobSummary.completedAt)}</s-text>
                  </s-stack>
                )}
                <s-stack gap="small">
                  <s-text color="subdued" size="small">Original Size</s-text>
                  <s-text size="small">
                    {jobSummary.totalOriginalSize > 0 
                      ? formatSize(jobSummary.totalOriginalSize) 
                      : 'Calculating...'}
                  </s-text>
                </s-stack>
                <s-stack gap="small">
                  <s-text color="subdued" size="small">Optimized Size</s-text>
                  <s-text size="small">
                    {jobSummary.totalOptimizedSize > 0
                      ? formatSize(jobSummary.totalOptimizedSize)
                      : 'In progress...'}
                  </s-text>
                </s-stack>
                {jobSummary.totalSavingsPercent !== null && jobSummary.totalSavingsPercent !== undefined && (
                  <s-stack gap="small">
                    <s-text color="subdued" size="small">Overall Savings</s-text>
                    <s-text tone="success" size="small" type="strong">
                      {parseFloat(jobSummary.totalSavingsPercent).toFixed(2)}%
                    </s-text>
                  </s-stack>
                )}
              </s-grid>
            </s-stack>
          </s-box>
        )}

        {/* Stats: Images and Products side-by-side */}
        <s-grid gridTemplateColumns="1fr 1fr" gap="base">
          {/* Image Stats */}
          <s-box padding="base" background="subdued" borderRadius="base">
            <s-stack gap="small-100">
              <s-text type="strong" tone="subdued" size="small">IMAGES</s-text>
              <s-stack direction="inline" gap="large">
                <s-stack gap="small" style={{ minWidth: '60px' }}>
                  <s-text type="strong">Total</s-text>
                  <s-heading>{total || 0}</s-heading>
                </s-stack>
                <s-stack gap="small" style={{ minWidth: '70px' }}>
                  <s-text type="strong">Completed</s-text>
                  <s-text tone="success">
                    <s-heading>{completed || 0}</s-heading>
                  </s-text>
                </s-stack>
                <s-stack gap="small" style={{ minWidth: '70px' }}>
                  <s-text type="strong">In Progress</s-text>
                  <s-text tone="warning">
                    <s-heading>{inProgress || 0}</s-heading>
                  </s-text>
                </s-stack>
                <s-stack gap="small" style={{ minWidth: '50px' }}>
                  <s-text type="strong">Failed</s-text>
                  <s-text tone="critical">
                    <s-heading>{failed || 0}</s-heading>
                  </s-text>
                </s-stack>
                {skipped > 0 && (
                  <s-stack gap="small" style={{ minWidth: '50px' }}>
                    <s-text type="strong">Skipped</s-text>
                    <s-text tone="info">
                      <s-heading>{skipped}</s-heading>
                    </s-text>
                  </s-stack>
                )}
                {revertedImagesCount > 0 && (
                  <s-stack gap="small" style={{ minWidth: '60px' }}>
                    <s-text type="strong">Reverted</s-text>
                    <s-text tone="attention">
                      <s-heading>{revertedImagesCount}</s-heading>
                    </s-text>
                  </s-stack>
                )}
                {(status === 'cancelled' || aborted > 0) && (
                  <s-stack gap="small" style={{ minWidth: '50px' }}>
                    <s-text type="strong">Aborted</s-text>
                    <s-text tone="attention">
                      <s-heading>{aborted || 0}</s-heading>
                    </s-text>
                  </s-stack>
                )}
              </s-stack>
            </s-stack>
          </s-box>

          {/* Product Stats */}
          {(() => {
            // Paged responses carry whole-job product counts (items only holds one page);
            // legacy responses (full item list) are counted from the items.
            const stats = productStats
              ? { ...productStats, seen: pagination ? pagination.totalProducts : 0 }
              : computeProductStats(items);
            // Use server-provided total (stable from start), fall back to seen products count
            const stableTotalProducts = totalProductsFromServer != null ? totalProductsFromServer : stats.seen;

            const completedProductsCount = stats.completed;
            const inProgressProductsCount = stats.inProgress;
            const failedProductsCount = stats.failed;
            const skippedProductsCount = stats.skipped;
            const revertedProductsCount = stats.reverted;

            return (
              <s-box padding="base" background="subdued" borderRadius="base">
                <s-stack gap="small-100">
                  <s-text type="strong" tone="subdued" size="small">PRODUCTS</s-text>
                  <s-stack direction="inline" gap="large">
                    <s-stack gap="small" style={{ minWidth: '50px' }}>
                      <s-text type="strong">Total</s-text>
                      <s-heading>{stableTotalProducts}</s-heading>
                    </s-stack>
                    <s-stack gap="small" style={{ minWidth: '70px' }}>
                      <s-text type="strong">Completed</s-text>
                      <s-text tone="success">
                        <s-heading>{completedProductsCount}</s-heading>
                      </s-text>
                    </s-stack>
                    <s-stack gap="small" style={{ minWidth: '70px' }}>
                      <s-text type="strong">In Progress</s-text>
                      <s-text tone="warning">
                        <s-heading>{inProgressProductsCount}</s-heading>
                      </s-text>
                    </s-stack>
                    {failedProductsCount > 0 && (
                      <s-stack gap="small" style={{ minWidth: '50px' }}>
                        <s-text type="strong">Failed</s-text>
                        <s-text tone="critical">
                          <s-heading>{failedProductsCount}</s-heading>
                        </s-text>
                      </s-stack>
                    )}
                    {skippedProductsCount > 0 && (
                      <s-stack gap="small" style={{ minWidth: '50px' }}>
                        <s-text type="strong">Skipped</s-text>
                        <s-text tone="info">
                          <s-heading>{skippedProductsCount}</s-heading>
                        </s-text>
                      </s-stack>
                    )}
                    {revertedProductsCount > 0 && (
                      <s-stack gap="small" style={{ minWidth: '60px' }}>
                        <s-stack direction="inline" gap="small" alignItems="center">
                          <s-text type="strong">Reverted</s-text>
                          <s-icon type="info" interestFor={`tt-rev-prod-${jobId}`}></s-icon>
                          <s-tooltip id={`tt-rev-prod-${jobId}`}>
                            Count of products with at least 1 image reverted — not necessarily all images reverted.
                          </s-tooltip>
                        </s-stack>
                        <s-text tone="attention">
                          <s-heading>{revertedProductsCount}</s-heading>
                        </s-text>
                      </s-stack>
                    )}
                  </s-stack>
                </s-stack>
              </s-box>
            );
          })()}
        </s-grid>

        {/* Product Accordion — groups items by product */}
        <ProductAccordion
          items={items}
          pagination={pagination}
          onPageChange={handlePageChange}
          pageLoading={pageLoading}
          processingProductKeys={processingProductKeys}
          jobId={jobId}
          jobStatus={status}
          expandedProducts={expandedProducts}
          toggleProduct={toggleProduct}
          selectedImages={selectedImages}
          toggleImageSelection={toggleImageSelection}
          toggleProductSelection={toggleProductSelection}
          handleBulkRevert={handleBulkRevert}
          revertingProductKey={revertingProductKey}
          revertedProducts={revertedProducts}
          navigate={navigate}
          setCompareImage={setCompareImage}
        />
        {error && (
          <s-banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </s-banner>
        )}
        </s-stack>
      {compareImage && (
        <CompareModal
          data={compareImage}
          onClose={() => setCompareImage(null)}
        />
      )}
    </s-box>
  );
}

/**
 * Product Accordion — shows job items grouped by product.
 * Each product is collapsible with its images, statuses, sizes, savings.
 */
function ProductAccordion({
  items, pagination, onPageChange, pageLoading = false, processingProductKeys = null,
  jobId, jobStatus, expandedProducts, toggleProduct,
  selectedImages, toggleImageSelection, toggleProductSelection,
  handleBulkRevert, revertingProductKey, revertedProducts = new Set(), navigate,
  setCompareImage,
}) {
  const isAnyReverting = revertingProductKey !== null;
  // Shop domain for "View in Admin" / "View in Storefront" links
  const shopDomain = (() => {
    if (!shopifyAppBridge?.config?.shop) return null;
    const shop = shopifyAppBridge.config.shop;
    return shop.includes('.myshopify.com') ? shop : `${shop}.myshopify.com`;
  })();
  const products = useMemo(() => groupItemsByProduct(items), [items]);
  const isTerminal = jobStatus === 'completed' || jobStatus === 'failed' || jobStatus === 'cancelled';

  const getProductStatus = useCallback((productImages) => {
    if (productImages.length === 0) return { label: 'Pending', tone: 'subdued' };
    // 'reverted' is only shown when ALL images are reverted (handled via isReverted flag above)
    if (productImages.some(i => i.status === 'processing')) return { label: 'Processing...', tone: 'warning' };
    if (productImages.some(i => i.status === 'cancelled' || i.status === 'aborted')) return { label: 'Aborted', tone: 'attention' };
    if (productImages.every(i => i.status === 'pending')) return { label: 'Pending...', tone: 'subdued' };
    if (productImages.every(i => i.status === 'completed' || i.status === 'optimized' || i.status === 'success')) return { label: 'Fully Optimized', tone: 'success' };
    if (productImages.every(i => i.status === 'failed')) return { label: 'Failed', tone: 'critical' };
    if (productImages.some(i => i.status === 'failed') && productImages.some(i => i.status === 'completed' || i.status === 'optimized')) return { label: 'Partially Optimized', tone: 'warning' };
    if (productImages.some(i => i.status === 'reverted')) return { label: 'Partially Optimized', tone: 'warning' };
    if (productImages.some(i => i.status === 'pending')) return { label: 'Pending...', tone: 'subdued' };
    return { label: 'Processing...', tone: 'warning' };
  }, []);

  const getImageStatusBadge = useCallback((status) => {
    switch (status) {
      case 'completed':
      case 'optimized':
      case 'success':
        return { label: 'Done', tone: 'success' };
      case 'processing':
        return { label: 'Processing...', tone: 'warning' };
      case 'pending':
        return { label: 'Pending...', tone: 'subdued' };
      case 'failed':
        return { label: 'Failed', tone: 'critical' };
      case 'cancelled':
      case 'aborted':
        return { label: 'Aborted', tone: 'attention' };
      case 'skipped':
        return { label: 'Skipped', tone: 'info' };
      case 'reverted':
        return { label: 'Reverted', tone: 'attention' };
      case 'deleted':
        return { label: 'Deleted', tone: 'critical' };
      default:
        return { label: 'Pending...', tone: 'subdued' };
    }
  }, []);

  // Pagination: with a paged server response (`pagination` present) `items` already holds only the
  // current page of products and the JobCard owns the page number (it is sent with every poll, so the
  // user stays on their page). Without it (full item list) the list is sliced here as before.
  const serverPaged = !!pagination;
  const [localPage, setLocalPage] = useState(1);
  const totalProductCount = serverPaged ? pagination.totalProducts : products.length;
  const totalPages = serverPaged
    ? Math.max(1, pagination.totalPages)
    : Math.max(1, Math.ceil(products.length / PRODUCTS_PER_PAGE));
  const currentPage = serverPaged ? pagination.page : Math.min(localPage, totalPages); // clamp if the list shrinks
  const pageStart = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const pagedProducts = serverPaged ? products : products.slice(pageStart, pageStart + PRODUCTS_PER_PAGE);

  // After a user-initiated page change, bring this job's card back into view (its top).
  // Flagged via a ref so the 2s data polling never triggers a scroll.
  const scrollToJobOnPageChangeRef = useRef(false);
  const changePage = (nextPage) => {
    scrollToJobOnPageChangeRef.current = true;
    if (serverPaged) onPageChange(nextPage);
    else setLocalPage(nextPage);
  };
  useEffect(() => {
    if (!scrollToJobOnPageChangeRef.current) return;
    scrollToJobOnPageChangeRef.current = false;
    document.querySelector(`[data-job-card-id="${jobId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentPage]);

  // Auto-expand every product that has an image in 'processing' (all pages, not just the
  // visible one, so it is already open when the user navigates to it). A paged response lists
  // these keys for the whole job; otherwise they come from the (full) item list.
  const processingKeysSig = (processingProductKeys || products
    .filter(p => p.images.some(i => i.status === 'processing'))
    .map(p => p.productId || p.productTitle))
    .join('|');
  useEffect(() => {
    if (!processingKeysSig) return;
    processingKeysSig.split('|').forEach(k => {
      if (!expandedProducts.has(k)) toggleProduct(k);
    });
  }, [processingKeysSig]);

  if (items.length === 0) {
    return (
      <s-box padding="base" background="subdued" borderRadius="base">
        <s-stack gap="small" alignItems="center">
          <s-spinner size="small" />
          <s-text tone="subdued">Waiting for images to process…</s-text>
        </s-stack>
      </s-box>
    );
  }

  return (
    <s-stack gap="small-200" style={{ marginTop: '24px' }}>
      {/* Header row */}
      <s-stack direction="inline" gap="base" alignItems="center" justifyContent="space-between">
        <s-text type="strong">Products ({totalProductCount})</s-text>
        <s-stack direction="inline" gap="small-200" alignItems="center">
          {selectedImages.size > 0 && (
            <s-button
              tone="critical"
              size="small"
              onClick={() => handleBulkRevert(null, [], 'bulk')}
              disabled={isAnyReverting}
              loading={revertingProductKey === 'bulk'}
            >
              Revert Selected ({selectedImages.size})
            </s-button>
          )}
          {isTerminal && (
            <s-button
              variant="secondary"
              size="small"
              onClick={() => navigate('/image-crush?tab=optimized')}
            >
              View All Optimized
            </s-button>
          )}
        </s-stack>
      </s-stack>

      {/* Product list */}
      <s-box border="base" borderRadius="base" paddingBlockStart="small-200">
        {pagedProducts.map((product, pageIdx) => {
          const pIdx = pageStart + pageIdx; // index in the full list (stable ids across pages)
          const key = product.productId || product.productTitle;
          const isExpanded = expandedProducts.has(key);
          const isLast = pageIdx === pagedProducts.length - 1;
          
          // Track product selection (excluding reverted/deleted)
          const completedKeys = product.images
            .map((img, idx) => ({ img, idx }))
            .filter(({ img }) => (img.status === 'completed' || img.status === 'optimized') && img.status !== 'reverted' && img.status !== 'deleted')
            .map(({ img, idx }) => selectionKey(img, `${key}-${idx}`));
          
          const allProductSelected = completedKeys.length > 0 && completedKeys.every(k => selectedImages.has(k));
          const someProductSelected = completedKeys.length > 0 && !allProductSelected && completedKeys.some(k => selectedImages.has(k));

          // Checkboxes stay DISABLED while ANY image is still pending/processing
          // (product not yet fully processed — don't allow partial revert mid-run)
          const hasUnfinishedImages = product.images.some(img => img.status === 'pending' || img.status === 'processing');
          
          // A product is "inactive" if all its images are marked deleted (removed/archived from Shopify)
          const isInactive = product.images.length > 0 && product.images.every(img => img.status === 'deleted');
          // A product is "reverted" if explicitly marked in state OR all images are 'reverted' in server data
          const isReverted = !isInactive && (revertedProducts.has(key) || product.images.every(img => img.status === 'reverted'));
          // Both reverted and inactive products should disable interaction
          const isDisabled = isReverted || isInactive;

          const pStatus = isInactive
            ? { label: 'Not active in Shopify', tone: 'critical' }
            : isReverted
            ? { label: 'Reverted', tone: 'attention' }
            : getProductStatus(product.images);

          // Product-level stats
          const pCompleted = product.images.filter(i => i.status === 'completed' || i.status === 'optimized').length;
          const pFailed = product.images.filter(i => i.status === 'failed').length;
          const pProcessing = product.images.filter(i => i.status === 'processing').length;
          const pPending = product.images.filter(i => i.status === 'pending').length;
          const pOrigKb = product.images.reduce((s, i) => s + (i.originalSize || 0), 0);
          const pOptKb = product.images.reduce((s, i) => s + (i.optimizedSize || 0), 0);
          const pSavedKb = Math.max(0, pOrigKb - pOptKb);

          const isAbortedProduct = !isInactive && !isReverted && product.images.length > 0 && product.images.every(img => img.status === 'cancelled' || img.status === 'aborted');

          // Derive numeric product ID for admin link
          const numericProductId = (() => {
            if (!product.productId) return null;
            const id = product.productId;
            if (typeof id === 'string' && id.startsWith('gid://')) return id.split('/').pop();
            return id;
          })();

          return (
            <s-box
              key={key}
              borderStyle={isLast ? 'none' : 'none none solid none'}
              border="base"
            >
              <s-box
                padding="base"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  if (e.target.closest('s-checkbox,s-button,button,input')) return;
                  toggleProduct(key);
                }}
              >
                <s-grid gridTemplateColumns="auto auto auto 1fr auto" gap="base" alignItems="center">
                  {/* Column 1: Expand */}
                  <s-button
                    variant="tertiary"
                    icon={isExpanded ? 'chevron-down' : 'chevron-right'}
                    onClick={(e) => { e.stopPropagation(); toggleProduct(key); }}
                  />

                  {/* Column 2: Checkbox Slot (Keeps grid stable even when checkbox hidden) */}
                  <s-box inlineSize="20px">
                      <s-checkbox
                        checked={allProductSelected}
                        indeterminate={someProductSelected}
                        onInput={(e) => { e.stopPropagation(); toggleProductSelection(product.images, key); }}
                        disabled={isDisabled || isAnyReverting || completedKeys.length === 0 || hasUnfinishedImages}
                      />
                  </s-box>

                  {/* Column 3: Product thumbnail (first available image) */}
                  {(() => {
                    const thumbUrl = product.images.find(i => i.imageUrl)?.imageUrl;
                    return thumbUrl ? (
                      <s-box border="base" borderRadius="base" overflow="hidden" inlineSize="36px" blockSize="36px" style={{ flexShrink: 0 }}>
                        <img src={thumbUrl} alt="" style={{ width: 36, height: 36, objectFit: 'cover', display: 'block' }} />
                      </s-box>
                    ) : (
                      <s-box inlineSize="36px" blockSize="36px" borderRadius="base" background="subdued"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <s-icon source="image" size="small" />
                      </s-box>
                    );
                  })()}

                  {/* Column 4: Info (1fr) */}
                  <s-stack gap="small-100" style={{ minWidth: 0 }}>
                    <s-stack direction="inline" gap="small-200" alignItems="center">
                      <s-text type="strong">{product.productTitle}</s-text>
                      <s-badge tone={pStatus.tone} size="small">{pStatus.label}</s-badge>
                    </s-stack>
                    <s-stack direction="inline" gap="small-200" alignItems="center" style={{ flexWrap: 'wrap' }}>
                      <s-text color="subdued" size="small">{product.images.length} images</s-text>
                      {pFailed > 0 && (
                        <s-badge tone="critical" size="small">· {pFailed} failed</s-badge>
                      )}
                      {!isReverted && pOrigKb > 0 && pOptKb > 0 && (
                        <s-text size="small" color="subdued">· {formatSizeKb(pOrigKb)} → {formatSizeKb(pOptKb)}</s-text>
                      )}
                      {!isReverted && pSavedKb > 0 && (
                        <s-badge tone="success" size="small">Saved {formatSizeKb(pSavedKb)}</s-badge>
                      )}
                    </s-stack>
                  </s-stack>

                  {/* Column 4: Actions Area (Always at end, min-width keeps layout stable) */}
                  <s-stack direction="inline" gap="small-200" alignItems="center" onClick={(e) => e.stopPropagation()} style={{ justifySelf: 'end', minWidth: '40px' }}>
                    {/* Individual Product Revert Button — hidden when already reverted/inactive */}
                    {pCompleted > 0 && !isDisabled && (
                      <s-button
                        size="small"
                        variant="secondary"
                        tone="critical"
                        disabled={isAnyReverting || pProcessing > 0}
                        loading={revertingProductKey === key}
                        onClick={() => {
                           const ids = product.images
                             .filter(i => (i.status === 'completed' || i.status === 'optimized') && i.imageDatabaseId)
                             .map(i => i.imageDatabaseId);
                           if (ids.length > 0) handleBulkRevert(ids, [key], key);
                           else showErrorToast('Could not find image IDs to revert. This job may predate the revert feature.');
                        }}
                      >
                        Revert ({pCompleted})
                      </s-button>
                    )}

                    {/* Three-dot menu — View in Admin / Storefront (hidden when reverted/inactive/aborted) */}
                    {!isDisabled && !isAbortedProduct && shopDomain && numericProductId && (
                      <>
                        <s-button variant="tertiary" icon="menu-vertical" commandFor={`job-product-menu-${jobId}-${pIdx}`} />
                        <s-menu id={`job-product-menu-${jobId}-${pIdx}`} accessibilityLabel="Product actions">
                          <s-button
                            icon="product"
                            onClick={() => window.open(`https://${shopDomain}/admin/products/${numericProductId}`, '_blank')}
                          >
                            View in Admin
                          </s-button>
                          <s-button
                            icon="external"
                            onClick={() => {
                              const handle = product.productHandle;
                              window.open(handle
                                ? `https://${shopDomain}/products/${handle}`
                                : `https://${shopDomain}/admin/products/${numericProductId}`, '_blank');
                            }}
                          >
                            View in Storefront
                          </s-button>
                        </s-menu>
                      </>
                    )}
                  </s-stack>
                </s-grid>
              </s-box>

              {/* Expanded: images */}
              {isExpanded && (
                <s-box paddingInline="base" paddingBlockStart="base" paddingBlockEnd="base" background="subdued">
                  <s-stack gap="small-100">
                    {product.images.map((img, imgIdx) => {
                      const imgKey = selectionKey(img, `${key}-${imgIdx}`);
                      const imgStatus = isInactive
                        ? { label: 'Not active in Shopify', tone: 'critical' }
                        : isReverted
                        ? { label: 'Reverted', tone: 'attention' }
                        : getImageStatusBadge(img.status);
                      // Compare is only allowed for completed, non-disabled images
                      const isCompleted = !isDisabled && (img.status === 'completed' || img.status === 'optimized');
                      const isProcessingImg = img.status === 'processing';
                      const canCompare = isCompleted && img.imageDatabaseId;

                      return (
                        <div
                          key={imgIdx}
                          className={`job-image-row${canCompare ? ' job-image-row--clickable' : ''}`}
                          onClick={() => { if (canCompare) setCompareImage(img); }}
                        >
                          <s-stack direction="inline" gap="base" alignItems="center">
                            {/* Checkbox (only for completed, non-disabled, fully-processed products) */}
                            {isCompleted && (
                              <s-checkbox
                                checked={selectedImages.has(imgKey)}
                                onInput={() => toggleImageSelection(imgKey)}
                                disabled={isAnyReverting || hasUnfinishedImages}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}

                            {/* Thumbnail */}
                            {img.imageUrl ? (
                              <s-box border="base" borderRadius="base" overflow="hidden" inlineSize="36px" blockSize="36px" style={{ flexShrink: 0 }}>
                                <img
                                  src={img.imageUrl}
                                  alt={img.imageName}
                                  style={{ width: 36, height: 36, objectFit: 'cover', display: 'block' }}
                                />
                              </s-box>
                            ) : (
                              <s-box inlineSize="36px" blockSize="36px" borderRadius="base" background="subdued"
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <s-text tone="subdued" size="small">🖼</s-text>
                              </s-box>
                            )}

                            {/* Image info */}
                            <s-stack gap="small-100" style={{ flex: 1, minWidth: 0 }}>
                              <div title={img.imageName} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, fontSize: 14, lineHeight: '20px', cursor: 'default' }}>
                                {img.imageName}
                              </div>
                              <s-stack direction="inline" gap="small-200" alignItems="center" style={{ flexWrap: 'wrap' }}>
                                <s-badge tone={imgStatus.tone} size="small">{imgStatus.label}</s-badge>
                                {isCompleted && img.originalSize && (
                                  <s-text size="small" color="subdued">
                                    {formatSizeKb(img.originalSize)} → {formatSizeKb(img.optimizedSize)}
                                  </s-text>
                                )}
                                {isCompleted && img.savingsPercent > 0 && (
                                  <s-badge tone="success" size="small">
                                    {parseFloat(img.savingsPercent).toFixed(1)}% saved
                                  </s-badge>
                                )}
                                {img.status === 'failed' && img.error && (
                                  <s-text tone="critical" size="small">{img.error}</s-text>
                                )}
                              </s-stack>
                            </s-stack>

                            {/* Eye icon hint for clickable images */}
                            {canCompare && (
                              <s-text size="small" tone="subdued">
                                <s-icon source="view" size="small" />
                              </s-text>
                            )}
                          </s-stack>
                        </div>
                      );
                    })}
                  </s-stack>
                </s-box>
              )}
            </s-box>
          );
        })}
      </s-box>

      {/* Pager — only once there is more than one page of products */}
      {totalProductCount > PRODUCTS_PER_PAGE && (
        <s-stack direction="inline" gap="base" alignItems="center" justifyContent="center" style={{ paddingTop: '12px' }}>
          <s-button
            variant="secondary"
            icon="chevron-left"
            onClick={() => changePage(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1 || pageLoading}
            accessibilityLabel="Previous page"
          />
          <s-text color="subdued">
            Page {currentPage} of {totalPages} ({totalProductCount} products)
          </s-text>
          <s-button
            variant="secondary"
            icon="chevron-right"
            onClick={() => changePage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages || pageLoading}
            accessibilityLabel="Next page"
          />
        </s-stack>
      )}
    </s-stack>
  );
}

/**
 * Main OptimizationProgress Component
 * Displays up to 2 concurrent job cards
 */
function OptimizationProgress({ jobIds = [], hasEverHadJobs = false, isLoading = false, onCreditChange = null }) {
  // Jobs are already sorted with latest first from Dashboard, so no need to reverse
  const allJobIds = Array.isArray(jobIds) ? [...jobIds] : []; // Already sorted: latest first

  // Track previous job IDs to detect NEW jobs
  const prevJobIdsRef = useRef([]);
  const isInitialMountRef = useRef(true);

  // REMOVED: Auto-scroll on new job detection
  // This was causing scroll on page reload when jobs are loaded from API
  // Scrolling is now ONLY handled by JobCard component when scrollToJob flag is set
  useEffect(() => {
    // On initial mount, set prevJobIds to current jobs to prevent "new job" detection
    if (isInitialMountRef.current) {
      prevJobIdsRef.current = allJobIds;
      isInitialMountRef.current = false;
      return;
    }

    // Update previous job IDs for future comparisons
    prevJobIdsRef.current = allJobIds;
  }, [allJobIds.join(','), isLoading]); // Use join to create stable dependency

  if (isLoading) {
    return (
      <s-stack gap="base">
        <s-heading>Optimization Progress</s-heading>
        <s-box padding="base" border="base" borderRadius="base">
          <s-stack gap="base">
            <s-stack direction="inline" gap="base" justifyContent="space-between" alignItems="center">
              <div className="shimmer-loader shimmer-text-medium" style={{ width: 140 }} />
              <div className="shimmer-loader shimmer-text-small" style={{ width: 80 }} />
            </s-stack>
            <div className="shimmer-loader" style={{ height: 8, width: '100%', borderRadius: 4 }} />
            <s-stack direction="inline" gap="base">
              <div className="shimmer-loader shimmer-text-small" style={{ width: 60 }} />
              <div className="shimmer-loader shimmer-text-small" style={{ width: 60 }} />
              <div className="shimmer-loader shimmer-text-small" style={{ width: 60 }} />
            </s-stack>
          </s-stack>
        </s-box>
      </s-stack>
    );
  }

  if (allJobIds.length === 0 && !hasEverHadJobs) {
    return (
      <s-stack gap="base">
        <s-heading>Optimization Progress</s-heading>
        <s-text tone="subdued">No optimization jobs yet. Start one from the Image Crush page.</s-text>
      </s-stack>
    );
  }

  if (allJobIds.length === 0) {
    return null;
  }

  return (
    <s-stack gap="base">
      <s-heading>Optimization Progress</s-heading>
      <s-text tone="subdued">
        Showing {allJobIds.length} job{allJobIds.length !== 1 ? 's' : ''} (maximum 2 concurrent running)
      </s-text>
      <s-stack gap="base">
        {allJobIds.map((jobId, index) => (
          <div key={jobId} data-job-card-id={jobId}>
            <JobCard jobId={jobId} jobIndex={index} isLatestJob={index === 0} onCreditChange={onCreditChange} />
          </div>
        ))}
      </s-stack>
    </s-stack>
  );
}

export default OptimizationProgress;
